import type {
  InsuranceCarrier,
  InsuranceCarrierCreate,
  InsuranceCarrierUpdate,
} from "@/types/insurance-carrier";

import { requestData } from "@/api/api-config";

// requestData swallows HTTP errors and resolves to null by default, which would let a
// rejected write look like a successful one: react-query never sees a rejection, so
// onError never fires and the editor reports "Carrier updated" on a 422. Every call
// here opts out so failures propagate.
const THROW_ON_ERROR = false;

/** Matches CARRIER_LIST_LIMIT in decoda/services/insurance_carriers.py. */
export const CARRIER_LIST_LIMIT = 500;

export async function listInsuranceCarriers(
  search?: string,
  limit?: number,
  includeInactivePlans?: boolean,
): Promise<InsuranceCarrier[] | null> {
  const searchParams: Record<string, string | number | boolean> = {};
  if (search) searchParams.search = search;
  if (limit) searchParams.limit = limit;
  if (includeInactivePlans) searchParams.include_inactive_plans = true;

  return await requestData<InsuranceCarrier[]>(
    "admin/insurance-carriers",
    "get",
    Object.keys(searchParams).length > 0 ? { searchParams } : undefined,
    THROW_ON_ERROR,
  );
}

export async function createInsuranceCarrier(
  data: InsuranceCarrierCreate,
): Promise<InsuranceCarrier | null> {
  return await requestData<InsuranceCarrier>(
    "admin/insurance-carriers",
    "post",
    { json: data },
    THROW_ON_ERROR,
  );
}

export async function updateInsuranceCarrier(
  carrierId: string,
  data: InsuranceCarrierUpdate,
): Promise<InsuranceCarrier | null> {
  return await requestData<InsuranceCarrier>(
    `admin/insurance-carriers/${carrierId}`,
    "put",
    { json: data },
    THROW_ON_ERROR,
  );
}

export async function archiveInsuranceCarrier(
  carrierId: string,
): Promise<void> {
  await requestData(
    `admin/insurance-carriers/${carrierId}`,
    "delete",
    undefined,
    THROW_ON_ERROR,
  );
}
